/** @jsxImportSource react */
import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from '../i18n'
import {
  Check,
  ArrowRight,
  Globe,
  Scale,
  Activity,
  Handshake,
  Compass,
  Plane,
  Ship,
  Search,
  Package,
  ClipboardCheck,
  DollarSign,
  Truck,
  Target,
  Heart,
  UsersRound,
  Languages,
  MapPin,
  PhoneCall,
  Mail,
  Twitter,
  Facebook,
  Linkedin,
  Instagram,
  ChevronDown,
  Star,
  ChevronRight,
  ChevronLeft,
  ArrowUp,
  Clock,
  Calendar,
  ShieldCheck,
  Medal,
  Zap,
} from 'lucide-react'

import { Logo } from '../components/Logo'
import zheerImg from '../Asset/ZheerMohammed.jpg'
import shvanImg from '../Asset/Shvan.jpg'
import haremImg from '../Asset/Harem.jpg'
import junoImg from '../Asset/juno.jpg'

const KurdistanFlag = ({ className = "w-full h-full" }: { className?: string }) => {
  return (
    <svg 
      viewBox="0 0 3 2" 
      className={`${className} shadow-sm rounded-sm`}
      id="kurdistan-flag-svg"
    >
      {/* Red strip */}
      <rect width="3" height="0.667" fill="#E31E20"/>
      {/* White strip */}
      <rect y="0.667" width="3" height="0.667" fill="#FFFFFF"/>
      {/* Green strip */}
      <rect y="1.334" width="3" height="0.667" fill="#07813F"/>
      {/* Yellow sun in the center */}
      <circle cx="1.5" cy="1" r="0.22" fill="#FDB913"/>
      {/* Sun rays represented as paths */}
      <path d="M1.5,0.73 L1.5,1.27 M1.23,1 L1.77,1 M1.31,0.81 L1.69,1.19 M1.31,1.19 L1.69,0.81 M1.4,0.76 L1.6,1.24 M1.4,1.24 L1.6,0.76 M1.26,0.9 L1.74,1.1 M1.26,1.1 L1.74,0.9" stroke="#FDB913" strokeWidth="0.045" strokeLinecap="round" />
    </svg>
  );
};

interface FlagImageProps {
  src: string;
  alt: string;
  className?: string;
}

const FlagImage: React.FC<FlagImageProps> = ({ src, alt, className = "w-full h-full" }) => {
  const isKurdish = src.includes('Flag_of_Kurdistan') || alt.toLowerCase().includes('kurdistan') || alt.toLowerCase().includes('kurd');
  
  if (isKurdish) {
    return <KurdistanFlag className={className} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

// Hook to detect device type
const useDeviceDetect = () => {
    const [device, setDevice] = useState({
      isMobile: false,
      isTablet: false,
      isDesktop: true,
    })
    useEffect(() => {
      const checkDevice = () => {
        const width = window.innerWidth
        setDevice({
          isMobile: width < 640,
          isTablet: width >= 640 && width < 1024,
          isDesktop: width >= 1024,
        })
      }
      checkDevice()
      window.addEventListener('resize', checkDevice)
      return () => window.removeEventListener('resize', checkDevice)
    }, [])
    return device
}

// Intersection Observer for animations
const useElementOnScreen = (options: any) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(false)
    useEffect(() => {
      const currentRef = containerRef.current;
      const observer = new IntersectionObserver(([entry]) => {
        if(entry.isIntersecting) {
            setIsVisible(true)
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        }
      }, options)
      if (currentRef) {
        observer.observe(currentRef)
      }
      return () => {
        if (currentRef) {
          observer.unobserve(currentRef)
        }
      }
    }, [options])
    return [containerRef, isVisible] as const
}

const AnimatedItem = ({ children, index }: { children: React.ReactNode, index: number }) => {
    const [ref, isVisible] = useElementOnScreen({
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
    })
    return (
        <div 
            ref={ref}
            className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
            style={{ transitionDelay: `${index * 200}ms` }}
        >
            {children}
        </div>
    )
}

export function LandingPage() {
  const { t, dir, language } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [stats, setStats] = useState({
    shipments: 0,
    countries: 0,
    clients: 0,
    years: 0,
  })
  const [isStatsVisible, setIsStatsVisible] = useState(false)
  const statsRef = useRef(null)
  const [activeProcess, setActiveProcess] = useState(0)
  const [activeService, setActiveService] = useState(0)
  const [activeFaq, setActiveFaq] = useState(0)
  // Mouse position for interactive elements
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0,
  })
  const { isMobile, isTablet, isDesktop } = useDeviceDetect()
  // Handle mouse movement for interactive elements
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY,
      })
    }
    if (!isMobile && !isTablet) {
      window.addEventListener('mousemove', handleMouseMove)
      return () => window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [isMobile, isTablet])
  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      // Update scroll progress for progress bar
      const totalHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight
      const progress = (window.scrollY / totalHeight) * 100
      setScrollProgress(progress)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  // Animate stats counter when in view
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isStatsVisible) {
          setIsStatsVisible(true)
          // Animate the stats counters
          const animateStats = () => {
            let startTime: number | null = null
            const duration = 2000 // 2 seconds
            const finalStats = {
              shipments: 25000,
              countries: 40,
              clients: 1200,
              years: 10,
            }
            const step = (timestamp: number) => {
              if (!startTime) startTime = timestamp
              const progress = Math.min((timestamp - startTime) / duration, 1)
              setStats({
                shipments: Math.floor(progress * finalStats.shipments),
                countries: Math.floor(progress * finalStats.countries),
                clients: Math.floor(progress * finalStats.clients),
                years: Math.floor(progress * finalStats.years),
              })
              if (progress < 1) {
                window.requestAnimationFrame(step)
              }
            }
            window.requestAnimationFrame(step)
          }
          animateStats()
        }
      })
    }, options)
    const currentRef = statsRef.current;
    if (currentRef) {
      observer.observe(currentRef)
    }
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [isStatsVisible])

  // Memoize testimonials data to prevent re-creation on re-renders
  const testimonials = useMemo(() => [
    {
      quote: t('staff_zheer_text'),
      name: t('staff_zheer_name'),
      position: t('staff_zheer_position'),
      country: '🇬🇧',
      image: zheerImg,
      flag: 'https://flagcdn.com/gb.svg',
      rating: 5,
    },
    {
      quote: t('staff_harem_text'),
      name: t('staff_harem_name'),
      position: t('staff_harem_position'),
      country: 'Kurd',
      image: haremImg,
      flag: 'https://upload.wikimedia.org/wikipedia/commons/d/d2/Flag_of_Kurdistan.svg',
      rating: 5,
    },
    {
      quote: t('staff_shvan_text'),
      name: t('staff_shvan_name'),
      position: t('staff_shvan_position'),
      country: 'Kurd',
      image: shvanImg,
      flag: 'https://upload.wikimedia.org/wikipedia/commons/d/d2/Flag_of_Kurdistan.svg',
      rating: 5,
    },
    {
      quote: t('staff_juno_text'),
      name: t('staff_juno_name'),
      position: t('staff_juno_position'),
      country: '🇨🇳',
      image: junoImg,
      flag: 'https://flagcdn.com/cn.svg',
      rating: 5,
    },
  ], [t]);
  
  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [testimonials.length])

  // Scroll to section smoothly
  const scrollToSection = (id: string) => {
    const section = document.getElementById(id)
    if (section) {
      section.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }
  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }
  // Process steps data
  const processSteps = [
    {
      icon: <ClipboardCheck size={24} />,
      number: t('numeral_1'),
      title: t('step1Title'),
      description: t('step1Desc'),
      color: 'from-gray-800 to-gray-700',
    },
    {
      icon: <DollarSign size={24} />,
      number: t('numeral_2'),
      title: t('step2Title'),
      description: t('step2Desc'),
      color: 'from-gray-800 to-gray-700',
    },
    {
      icon: <Truck size={24} />,
      number: t('numeral_3'),
      title: t('step3Title'),
      description: t('step3Desc'),
      color: 'from-gray-800 to-gray-700',
    },
    {
      icon: <Package size={24} />,
      number: t('numeral_4'),
      title: t('step4Title'),
      description: t('step4Desc'),
      color: 'from-gray-800 to-gray-700',
    },
  ]
  // Global shipping locations
  const shippingLocations = useMemo(() => [
    {
      name: 'Tag Express',
      city: t('city_shanghai'),
      country: t('country_china'),
      flag: 'https://flagcdn.com/cn.svg',
      image:
        'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      languages: [t('lang_mandarin'), t('lang_english')],
      description: t('hub_china_desc'),
    },
    {
      name: 'Tag Express',
      city: t('city_dubai'),
      country: t('country_uae'),
      flag: 'https://flagcdn.com/ae.svg',
      image:
        'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      languages: [t('lang_arabic'), t('lang_english'), t('lang_hindi')],
      description: t('hub_uae_desc'),
    },
    {
      name: 'Tag Express',
      city: t('city_sulaymaniyah'),
      country: t('country_kurdistan_iraq'),
      flag: 'https://upload.wikimedia.org/wikipedia/commons/d/d2/Flag_of_Kurdistan.svg',
      image:
        'https://images.unsplash.com/photo-1621682372775-533449e550ed?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      languages: [t('lang_kurdish'), t('lang_arabic'), t('lang_english')],
      description: t('hub_iraq_desc'),
    },
    {
      name: 'Tag Express',
      city: t('city_kuwait'),
      country: t('country_kuwait'),
      flag: 'https://flagcdn.com/kw.svg',
      image:
        'https://images.unsplash.com/photo-1518005020951-eccb494ad742?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      languages: [t('lang_arabic'), t('lang_english')],
      description: t('hub_kuwait_desc'),
    },
    {
      name: 'Tag Express',
      city: t('city_london'),
      country: t('country_uk'),
      flag: 'https://flagcdn.com/gb.svg',
      image:
        'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      languages: [t('lang_english')],
      description: t('hub_uk_desc'),
    },
    {
      name: 'Tag Express',
      city: t('city_new_york'),
      country: t('country_usa'),
      flag: 'https://flagcdn.com/us.svg',
      image:
        'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      languages: [t('lang_english')],
      description: t('hub_usa_desc'),
    },
  ], [t])
  // Country count based on Where We Work section
  const uniqueCountriesCount = useMemo(() => {
    return new Set(shippingLocations.map((loc) => loc.country)).size
  }, [shippingLocations])

  const acrossCountriesText = useMemo(() => {
    const count = uniqueCountriesCount
    if (language.startsWith('ku')) {
      return `لە سەرانسەری ${count} وڵاتدا`
    } else if (language.startsWith('ar')) {
      return `عبر ${count} دول`
    }
    return `Across ${count} countries`
  }, [uniqueCountriesCount, language])

  // Why choose us points
  const whyUsPoints = [
    {
      icon: <Globe size={24} />,
      title: t('whyUs_globalNetwork_title'),
      description: t('whyUs_globalNetwork_text'),
    },
    {
      icon: <Scale size={24} />,
      title: t('whyUs_transparentPricing_title'),
      description: t('whyUs_transparentPricing_text'),
    },
    {
      icon: <Activity size={24} />,
      title: t('whyUs_endToEndService_title'),
      description: t('whyUs_endToEndService_text'),
    },
    {
      icon: <Handshake size={24} />,
      title: t('whyUs_dedicatedSupport_title'),
      description: t('whyUs_dedicatedSupport_text'),
    },
    {
      icon: <Compass size={24} />,
      title: t('whyUs_localExpertise_title'),
      description: t('whyUs_localExpertise_text'),
    },
    {
      icon: <Plane size={24} />,
      title: t('whyUs_flexibleShipping_title'),
      description: t('whyUs_flexibleShipping_text'),
    },
  ]

  // Services data
  const services = [
    {
      icon: <Search size={24} />,
      title: t('sourcing_title'),
      description: t('sourcing_desc'),
      features: [t('sourcing_f1'), t('sourcing_f2'), t('sourcing_f3')],
      image:
        'https://images.unsplash.com/photo-1598257006458-087169a1f08d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
    },
    {
      icon: <Ship size={24} />,
      title: t('shipping_title'),
      description: t('shipping_desc'),
      features: [t('shipping_f1'), t('shipping_f2'), t('shipping_f3')],
      image:
        'https://images.unsplash.com/photo-1494412519320-aa613dfb7738?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
    },
    {
      icon: <Scale size={24} />,
      title: t('customs_title'),
      description: t('customs_desc'),
      features: [t('customs_f1'), t('customs_f2'), t('customs_f3')],
      image:
        'https://images.unsplash.com/photo-1580674285054-bed31e145f59?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80',
    },
  ]
  // FAQ data
  const faqData = [
    { question: t('faq_q1'), answer: t('faq_a1') },
    { question: t('faq_q2'), answer: t('faq_a2') },
    { question: t('faq_q3'), answer: t('faq_a3') },
    { question: t('faq_q4'), answer: t('faq_a4') },
    { question: t('faq_q5'), answer: t('faq_a5') },
    { question: t('faq_q6'), answer: t('faq_a6') },
  ]
  return (
    <div className="w-full min-h-screen overflow-x-hidden">
      {/* Scroll Progress Bar */}
      <div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 z-50 transition-all duration-300"
        style={{
          width: `${scrollProgress}%`,
        }}
      ></div>
      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-40 bg-black text-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-800 transition-all duration-300 ${scrollProgress > 20 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}
        aria-label="Back to top"
      >
        <ArrowUp size={20} />
      </button>
      {/* Hero Section - Dynamic Image Background */}
      <section className="relative w-full min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80"
            alt="Global shipping"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/80"></div>
        </div>
        <div className="container mx-auto px-2 md:px-4 py-12 md:py-20 relative z-10 w-full">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/10 text-white backdrop-blur-sm mb-4 animate-fade-in border border-white/20">
              {t('global_logistics')}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-up tracking-tight">
              <span className="text-white">{t('hero_title')}</span>{' '}
              <span className="block text-gray-300 mt-2">
                {t('hero_subtitle')}
              </span>
            </h1>
            <p
              className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-10 animate-fade-up"
              style={{
                animationDelay: '200ms',
              }}
            >
              {t('hero_description')}
            </p>
            <div
              className="flex flex-wrap gap-4 justify-center animate-fade-up"
              style={{
                animationDelay: '300ms',
              }}
            >
              <Link to="/signup" className="relative overflow-hidden bg-white text-black px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-medium flex items-center transform hover:translate-y-[-2px] group">
                <span className="relative z-10">{t('getStarted')}</span>
                <ArrowRight
                  size={18}
                  className={`relative z-10 ${dir === 'ltr' ? 'ms-2' : 'me-2 rotate-180'} group-hover:translate-x-1 transition-transform`}
                />
              </Link>
              <button
                className="border border-white/50 hover:border-white bg-black/30 backdrop-blur-sm text-white px-8 py-4 rounded-xl transition-all duration-300 font-medium flex items-center hover:bg-black/50"
                onClick={() => scrollToSection('process')}
              >
                {t('seeHowItWorks')}
                <ChevronDown
                  size={18}
                  className={`${dir === 'ltr' ? 'ms-2' : 'me-2'}`}
                />
              </button>
            </div>
          </div>
        </div>
        {/* Interactive mouse follower */}
        {isDesktop && (
          <div
            className="hidden md:block absolute w-32 h-32 rounded-full bg-white/10 blur-xl pointer-events-none mix-blend-overlay"
            style={{
              left: `${mousePosition.x}px`,
              top: `${mousePosition.y}px`,
              transform: 'translate(-50%, -50%)',
              transition: 'transform 0.1s ease-out',
            }}
          ></div>
        )}
        {/* Floating ship icon */}
        <div className="absolute bottom-10 right-10 text-white/30 animate-float">
          <Ship size={40} />
        </div>
        {/* Floating plane icon */}
        <div className="absolute top-20 left-10 text-white/30 animate-float-slow">
          <Plane size={40} />
        </div>
      </section>
      {/* Stats Section - Modern Cards with Smooth Hover */}
      <section className="py-16 md:py-24 relative overflow-hidden bg-white">
        <div className="container mx-auto px-2 md:px-4 relative">
          <div
            ref={statsRef}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto"
          >
            <div className="bg-white rounded-xl shadow-md p-8 text-center border border-gray-100 group hover:border-gray-200 transition-all duration-500 ease-in-out hover:shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center text-gray-800 mx-auto mb-6 group-hover:bg-gradient-to-br group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-500 ease-in-out">
                <Truck
                  size={28}
                  className="transition-all duration-500 ease-in-out group-hover:scale-110"
                />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2 transition-all duration-500 ease-in-out group-hover:text-gray-800">
                {stats.shipments.toLocaleString()}+
              </div>
              <p className="text-gray-600 transition-all duration-500 ease-in-out group-hover:text-gray-700">
                {t('shipmentsDelivered')}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-8 text-center border border-gray-100 group hover:border-gray-200 transition-all duration-500 ease-in-out hover:shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center text-gray-800 mx-auto mb-6 group-hover:bg-gradient-to-br group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-500 ease-in-out">
                <Globe
                  size={28}
                  className="transition-all duration-500 ease-in-out group-hover:scale-110"
                />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2 transition-all duration-500 ease-in-out group-hover:text-gray-800">
                {stats.countries}+
              </div>
              <p className="text-gray-600 transition-all duration-500 ease-in-out group-hover:text-gray-700">
                {t('countriesServed')}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-8 text-center border border-gray-100 group hover:border-gray-200 transition-all duration-500 ease-in-out hover:shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center text-gray-800 mx-auto mb-6 group-hover:bg-gradient-to-br group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-500 ease-in-out">
                <UsersRound
                  size={28}
                  className="transition-all duration-500 ease-in-out group-hover:scale-110"
                />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2 transition-all duration-500 ease-in-out group-hover:text-gray-800">
                {stats.clients.toLocaleString()}+
              </div>
              <p className="text-gray-600 transition-all duration-500 ease-in-out group-hover:text-gray-700">
                {t('satisfiedClients')}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-8 text-center border border-gray-100 group hover:border-gray-200 transition-all duration-500 ease-in-out hover:shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center text-gray-800 mx-auto mb-6 group-hover:bg-gradient-to-br group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-500 ease-in-out">
                <Calendar
                  size={28}
                  className="transition-all duration-500 ease-in-out group-hover:scale-110"
                />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2 transition-all duration-500 ease-in-out group-hover:text-gray-800">
                {stats.years}+
              </div>
              <p className="text-gray-600 transition-all duration-500 ease-in-out group-hover:text-gray-700">
                {t('yearsOfExperience')}
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Services Section - Redesigned Modern & Minimalist */}
      <section className="py-20 md:py-28 relative overflow-hidden bg-gray-100">
        <div className="container mx-auto px-2 md:px-4 relative">
          {/* Section header */}
          <div className="mb-16 text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white mb-4">
              {t('services')}
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 text-gray-900">
              {t('services_title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('services_subtitle')}
            </p>
          </div>
          {/* Desktop Layout - Modern and Minimalist */}
          <div className="hidden lg:block">
            <div className="flex flex-col space-y-12 max-w-6xl mx-auto">
              {services.map((service, index) => (
                <div
                  key={index}
                  className={`flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} bg-white rounded-3xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-all duration-500 group`}
                >
                  {/* Image Section */}
                  <div className="w-1/2 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors duration-500"></div>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div
                      className={`absolute ${index % 2 === 0 ? 'left-0 rounded-tr-2xl rounded-br-2xl' : 'right-0 rounded-tl-2xl rounded-bl-2xl'} top-1/2 -translate-y-1/2 w-12 h-24 bg-white group-hover:bg-gray-800 transition-colors duration-500`}
                    ></div>
                  </div>
                  {/* Content Section */}
                  <div className="w-1/2 p-12 flex flex-col justify-center">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-500">
                        {service.icon}
                      </div>
                      <h3 className="text-2xl font-bold ms-5 text-gray-900 group-hover:text-gray-700 transition-colors duration-300">
                        {service.title}
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                      {service.description}
                    </p>
                    <div className="space-y-3 mb-8">
                      {service.features.map((feature, i) => (
                        <div key={i} className="flex items-center">
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-gray-800 me-3 group-hover:bg-gray-800 group-hover:text-white transition-colors duration-300">
                            <Check size={14} />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Tablet Layout - Modern Cards */}
          <div className="hidden sm:block lg:hidden">
            <div className="grid grid-cols-1 gap-10">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden group"
                >
                  {/* Service header with image */}
                  <div className="relative h-64">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 flex items-center">
                      <div className="w-14 h-14 bg-gray-800 rounded-xl flex items-center justify-center text-white shadow-lg me-4 group-hover:scale-110 transition-transform duration-300">
                        {service.icon}
                      </div>
                      <h3 className="text-xl font-bold text-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="p-6">
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <div className="space-y-3 mb-6">
                      {service.features.map((feature, i) => (
                        <div key={i} className="flex items-center">
                          <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-800 me-3">
                            <Check size={12} />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Mobile Layout - Interactive Cards */}
          <div className="sm:hidden">
            <div className="relative">
              {/* Service selector */}
              <div className="flex mb-6 border-b border-gray-200">
                {services.map((service, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveService(index)}
                    className={`flex-1 py-3 px-2 text-center text-sm font-medium relative ${activeService === index ? 'text-gray-900' : 'text-gray-500'}`}
                  >
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${activeService === index ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500'}`}
                      >
                        {service.icon}
                      </div>
                      <span className="line-clamp-1">{service.title}</span>
                    </div>
                    {activeService === index && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800"></div>
                    )}
                  </button>
                ))}
              </div>
              {/* Active service content */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="h-48 relative">
                  <img
                    src={services[activeService].image}
                    alt={services[activeService].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-xl font-bold text-white">
                      {services[activeService].title}
                    </h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-gray-600 text-sm mb-4">
                    {services[activeService].description}
                  </p>
                  <div className="space-y-2 mb-5">
                    {services[activeService].features.map((feature, i) => (
                      <div key={i} className="flex items-center text-sm">
                        <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-gray-800 me-2">
                          <Check size={10} />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Pagination dots */}
              <div className="flex justify-center mt-4 gap-x-1">
                {services.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveService(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${activeService === index ? 'bg-gray-800' : 'bg-gray-300'}`}
                    aria-label={`Go to service ${index + 1}`}
                  ></button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Process Section - Modern Minimalist Redesign */}
      <section
        id="process"
        className="py-24 md:py-32 bg-white relative overflow-hidden"
      >
        <div className="container mx-auto px-2 md:px-4 relative">
          {/* Section header */}
          <div className="mb-20 text-center">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white mb-4">
              {t('how_we_work')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              {t('process_title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('process_subtitle')}
            </p>
          </div>
          {/* Desktop Process Steps - Interactive Timeline */}
           <div className="hidden lg:block max-w-6xl mx-auto relative">
            {/* The line itself, positioned at the bottom, behind the numbers */}
            <div className="absolute bottom-[56px] left-[12.5%] right-[12.5%] h-1 bg-gray-200 rounded-full z-0">
                <div
                    className={`absolute top-0 h-full bg-gray-800 rounded-full transition-all duration-700 ${dir === 'ltr' ? 'left-0' : 'right-0'}`}
                    style={{
                        width: `${(activeProcess / (processSteps.length - 1)) * 100}%`,
                    }}
                ></div>
            </div>

            <div className="grid grid-cols-4 gap-6 relative z-10">
                {processSteps.map((step, index) => (
                    <div
                        key={index}
                        className="relative pb-24" // Added padding for the number
                        onMouseEnter={() => setActiveProcess(index)}
                    >
                        {/* Content card */}
                        <div
                            className={`pt-8 pb-8 px-5 rounded-xl transition-all duration-500 h-full ${activeProcess === index ? 'bg-gray-800 text-white shadow-xl transform -translate-y-2' : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm'}`}
                        >
                            <div
                                className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 mx-auto transition-all duration-300 ${activeProcess === index ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700'}`}
                            >
                                {step.icon}
                            </div>
                            <h3 className={`text-lg font-bold mb-3 text-center ${activeProcess === index ? 'text-white' : 'text-gray-800'}`}>
                                {step.title}
                            </h3>
                            <p className={`text-center text-sm ${activeProcess === index ? 'text-white/90' : 'text-gray-600'}`}>
                                {step.description}
                            </p>
                        </div>

                        {/* Step number at the bottom */}
                        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
                            <div
                                className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300 ${activeProcess >= index ? 'bg-gray-800 text-white scale-110 shadow-lg' : 'bg-white border-2 border-gray-300 text-gray-500'}`}
                            >
                                {step.number}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
          </div>
          {/* Tablet Process Steps */}
          <div className="hidden md:block lg:hidden">
            <div className="grid grid-cols-2 gap-6">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className={`bg-white p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer
                    ${activeProcess === index ? 'border-gray-800 shadow-lg transform -translate-y-1' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}`}
                  onClick={() => setActiveProcess(index)}
                >
                  <div className="flex items-center mb-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold me-4
                      ${activeProcess === index ? 'bg-gray-800 text-white' : 'bg-gray-100 border border-gray-300 text-gray-500'}`}
                    >
                      {step.number}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {step.title}
                    </h3>
                  </div>
                  <div className="flex items-start">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 me-4
                      ${activeProcess === index ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                      {step.icon}
                    </div>
                    <p className="text-gray-600 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Mobile Process Steps - Interactive Accordion */}
          <div className="md:hidden space-y-4">
            {processSteps.map((step, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-gray-200 transition-all duration-300"
              >
                <button
                  onClick={() =>
                    setActiveProcess(activeProcess === index ? -1 : index)
                  }
                  className={`w-full flex items-center justify-between p-4 text-left
                    ${activeProcess === index ? 'bg-gray-800 text-white' : 'bg-white text-gray-800 hover:bg-gray-50'}`}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold me-3
                      ${activeProcess === index ? 'bg-white text-gray-800' : 'bg-gray-100 border border-gray-300 text-gray-600'}`}
                    >
                      {step.number}
                    </div>
                    <h3 className="font-bold">{step.title}</h3>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`transition-transform duration-300 ${activeProcess === index ? 'rotate-180 text-white' : 'text-gray-500'}`}
                  />
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden
                    ${activeProcess === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div
                    className={`p-4 flex items-start border-t ${activeProcess === index ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-100'}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white bg-gray-600 flex-shrink-0 me-3">
                      {step.icon}
                    </div>
                    <p className="text-sm">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Global Hubs Section - Modern Cards */}
      <section
        id="global-hubs"
        className="py-20 md:py-28 bg-gray-50 relative overflow-hidden"
      >
        <div className="container mx-auto px-2 md:px-4 relative">
          {/* Section header */}
          <div className="mb-16 text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white mb-4">
              {t('global_network')}
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 text-gray-900">
              {t('global_hubs_title')}
            </h2>
            <p className="text-lg text-gray-600">{t('global_hubs_subtitle')}</p>
          </div>
          {/* Locations grid with hover effect */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {shippingLocations.map((location, index) => (
              <AnimatedItem key={index} index={index}>
                <div
                  className="group h-full cursor-pointer"
                  onMouseMove={(e: React.MouseEvent<HTMLDivElement>) => {
                      if (isDesktop) {
                        const bounds = e.currentTarget.getBoundingClientRect()
                        const x = e.clientX - bounds.left
                        const y = e.clientY - bounds.top
                        const rotateX =
                          ((y - bounds.height / 2) / bounds.height) * -5
                        const rotateY =
                          ((x - bounds.width / 2) / bounds.width) * 5
                        e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
                      }
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                      e.currentTarget.style.transform =
                        'perspective(1000px) rotateX(0deg) rotateY(0deg)'
                    }}
                  >
                    <div className="relative bg-white h-full overflow-hidden rounded-2xl shadow-xl border border-gray-100 hover:border-gray-200 transition-all duration-500">
                      {/* Location image with overlay */}
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={location.image}
                          alt={location.city}
                          className="w-full h-full object-cover group-hover:scale-110 transition-all duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                        {/* Flag badge */}
                        <div className="absolute top-4 right-4 z-20 w-10 h-6 overflow-hidden shadow-md transform group-hover:scale-110 transition-transform">
                          <FlagImage
                            src={location.flag}
                            alt={`${location.country} flag`}
                            className="w-full h-full"
                          />
                        </div>
                        {/* Location info */}
                        <div className="absolute bottom-4 left-4 right-4 z-20">
                          <h3 className="text-2xl font-bold text-white">
                            {location.country}
                          </h3>
                          <div className="flex items-center text-white/90">
                            <MapPin
                              size={16}
                              className={`${dir === 'ltr' ? 'me-2' : 'ms-2'}`}
                            />
                            <span className="text-lg">{location.city}</span>
                          </div>
                        </div>
                      </div>
                      {/* Content */}
                      <div className="p-8">
                        <p className="text-gray-600 mb-6 text-lg">
                          {location.description}
                        </p>
                        {/* Languages */}
                        <div className="border-t border-gray-100 pt-6">
                          <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-4 flex items-center">
                            <Languages
                              size={16}
                              className={dir === 'ltr' ? 'me-2' : 'ms-2'}
                            />
                            {t('languages')}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {location.languages.map((lang, i) => (
                              <span
                                key={i}
                                className="inline-block py-1.5 px-3 text-sm bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors duration-300"
                              >
                                {lang}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              </AnimatedItem>
            ))}
          </div>
        </div>
      </section>
      {/* Testimonials Section - Modern Interactive Design */}
      <section
        id="testimonials"
        className="py-24 md:py-32 bg-white relative overflow-hidden"
      >
        <div className="container mx-auto px-2 md:px-4 relative">
          {/* Section header */}
          <div className="mb-16 text-center">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white mb-4">
              {t('testimonials')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              {t('testimonials_title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('testimonials_subtitle')}
            </p>
          </div>
           {/* Desktop Testimonial Grid */}
          <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 flex flex-col h-full">
                    {/* Country flag and rating */}
                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center">
                            <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={16} className={i < testimonial.rating ? 'text-gray-900 fill-gray-900' : 'text-gray-200'} />
                                ))}
                            </div>
                        </div>
                        <FlagImage src={testimonial.flag} alt={testimonial.country} className="w-6 h-4" />
                    </div>
                    {/* Quote */}
                    <div className="relative mb-8 flex-grow">
                        <span className="absolute top-0 left-0 text-5xl text-gray-200 leading-none font-serif">"</span>
                        <p className="text-gray-700 relative z-10 pt-4 ps-6 italic">{testimonial.quote}</p>
                    </div>
                    {/* Author */}
                    <div className="flex items-center mt-auto pt-4 border-t border-gray-100">
                        <div className="w-12 h-12 rounded-full overflow-hidden me-4 border-2 border-gray-200">
                            <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                        </div>
                        <div>
                            <p className="font-bold text-gray-900">{testimonial.name}</p>
                            <p className="text-gray-500 text-sm">{testimonial.position}</p>
                        </div>
                    </div>
                </div>
            ))}
          </div>

          {/* Mobile & Tablet Testimonial Design - Card Swipe */}
          <div className="md:hidden">
            <div className="relative overflow-hidden max-w-md mx-auto">
              {/* Testimonial card */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                {/* Country flag */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={
                          i < testimonials[activeTestimonial].rating
                            ? 'text-gray-900 fill-gray-900'
                            : 'text-gray-200'
                        }
                      />
                    ))}
                  </div>
                  <FlagImage
                    src={testimonials[activeTestimonial].flag}
                    alt={testimonials[activeTestimonial].country}
                    className="w-5 h-3.5"
                  />
                </div>
                {/* Quote */}
                <p className="text-gray-700 mb-6 text-sm relative">
                  <span className="absolute -top-2 -left-1 text-4xl text-gray-200 leading-none font-serif">
                    "
                  </span>
                  <span className="relative z-10 ps-4">
                    {testimonials[activeTestimonial].quote}
                  </span>
                </p>
                {/* Author */}
                <div className="flex items-center pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full overflow-hidden me-3">
                    <img
                      src={testimonials[activeTestimonial].image}
                      alt={testimonials[activeTestimonial].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">
                      {testimonials[activeTestimonial].name}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {testimonials[activeTestimonial].position}
                    </p>
                  </div>
                </div>
              </div>
              {/* Swipe indicators & navigation */}
              <div className="flex justify-between items-center mt-6">
                <button
                  onClick={() =>
                    setActiveTestimonial((prev) =>
                      prev === 0 ? testimonials.length - 1 : prev - 1,
                    )
                  }
                  className="w-10 h-10 rounded-full bg-white shadow border border-gray-200 flex items-center justify-center text-gray-600"
                  aria-label="Previous testimonial"
                >
                  {dir === 'ltr' ? (
                    <ChevronLeft size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                </button>
                <div className="flex gap-x-1.5">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTestimonial(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 
                        ${activeTestimonial === index ? 'bg-gray-800 w-5' : 'bg-gray-300'}`}
                      aria-label={`Go to testimonial ${index + 1}`}
                    ></button>
                  ))}
                </div>
                <button
                  onClick={() =>
                    setActiveTestimonial((prev) =>
                      prev === testimonials.length - 1 ? 0 : prev + 1,
                    )
                  }
                  className="w-10 h-10 rounded-full bg-white shadow border border-gray-200 flex items-center justify-center text-gray-600"
                  aria-label="Next testimonial"
                >
                  {dir === 'ltr' ? (
                    <ChevronRight size={18} />
                  ) : (
                    <ChevronLeft size={18} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Why Choose Us Section - Modern Interactive Cards */}
      <section
        id="why-us"
        className="py-24 md:py-32 bg-gray-50 relative overflow-hidden"
      >
        <div className="container mx-auto px-2 md:px-4 relative">
          {/* Section header */}
          <div className="mb-16 text-center">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white mb-4">
              {t('our_advantage')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              {t('whyUs_title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('whyUs_subtitle')}
            </p>
          </div>
          {/* Features grid - Desktop */}
          <div className="hidden lg:grid grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyUsPoints.map((point, index) => (
              <AnimatedItem key={index} index={index}>
                <div
                  className="bg-white rounded-xl shadow-md overflow-hidden group transition-all duration-700 hover:shadow-xl transform hover:-translate-y-1"
                >
                  <div className={`h-2 bg-gray-800 w-full transform ${dir === 'ltr' ? 'origin-left' : 'origin-right'} scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
                  <div className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center text-gray-700 mb-6 group-hover:bg-gray-800 group-hover:text-white transition-all duration-300 transform group-hover:rotate-3">
                      {point.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {point.title}
                    </h3>
                    <p className="text-gray-600">{point.description}</p>
                  </div>
                </div>
              </AnimatedItem>
            ))}
          </div>
          {/* Features grid - Tablet */}
          <div className="hidden md:grid lg:hidden grid-cols-2 gap-6 max-w-4xl mx-auto">
            {whyUsPoints.map((point, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-6 border border-gray-100 hover:border-gray-300 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
              >
                <div className="flex items-start">
                  <div className="w-12 h-12 rounded-lg border border-gray-200 flex items-center justify-center text-gray-700 me-4 transition-all duration-300 group-hover:border-gray-900 group-hover:text-gray-900">
                    {point.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {point.title}
                    </h3>
                    <p className="text-gray-600 text-sm">{point.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Mobile Features - Interactive Expandable Cards */}
          <div className="md:hidden space-y-4">
            {whyUsPoints.map((point, index) => {
              const isActive = index === activeTab
              return (
                <div
                  key={index}
                  className={`overflow-hidden rounded-xl transition-all duration-500 ${isActive ? 'bg-gray-800 text-white shadow-lg' : 'bg-white border border-gray-200'}`}
                >
                  <button
                    onClick={() => setActiveTab(isActive ? -1 : index)}
                    className="w-full flex items-center p-4 text-left"
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center me-3 flex-shrink-0 transition-all duration-300 ${isActive ? 'bg-white text-gray-800' : 'bg-gray-100 text-gray-700'}`}
                    >
                      {point.icon}
                    </div>
                    <h3
                      className={`font-bold ${isActive ? 'text-white' : 'text-gray-800'}`}
                    >
                      {point.title}
                    </h3>
                    <ChevronDown
                      size={18}
                      className={`ms-auto transition-transform duration-300 ${isActive ? 'rotate-180 text-white' : 'text-gray-500'}`}
                    />
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${isActive ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="p-4 pt-0 ps-16">
                      <p
                        className={`text-sm ${isActive ? 'text-white/90' : 'text-gray-600'}`}
                      >
                        {point.description}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      {/* About Section - Light Background */}
      <section
        id="about"
        className="py-20 md:py-28 bg-white relative overflow-hidden"
      >
        <div className="container mx-auto px-2 md:px-4 relative">
          <div className="max-w-6xl mx-auto">
            {/* Section header */}
            <div className="mb-16 text-center">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white mb-4">
                {t('our_story')}
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 text-gray-900">
                {t('about_title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('aboutUs_desc')}
              </p>
            </div>
            {/* Content layout */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Image with floating elements */}
              <div
                className={`${dir === 'ltr' ? 'md:order-1' : 'md:order-2'} relative`}
              >
                <div
                  className="relative group/image cursor-pointer"
                  onMouseMove={(e: React.MouseEvent<HTMLDivElement>) => {
                    if (isDesktop) {
                      const bounds = e.currentTarget.getBoundingClientRect()
                      const x = e.clientX - bounds.left
                      const y = e.clientY - bounds.top
                      const rotateX =
                        ((y - bounds.height / 2) / bounds.height) * -5
                      const rotateY =
                        ((x - bounds.width / 2) / bounds.width) * 5
                      e.currentTarget.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
                    }
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.currentTarget.style.transform =
                      'perspective(1000px) rotateX(0deg) rotateY(0deg)'
                  }}
                >
                  {/* Decorative elements */}
                  <div className="absolute -right-6 -bottom-6 w-full h-full border-2 border-gray-200 rounded-2xl transform translate-x-3 translate-y-3 group-hover/image:translate-x-6 group-hover/image:translate-y-6 transition-all duration-500"></div>
                  {/* Main image */}
                  <img
                    src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
                    alt="Shipping and Logistics"
                    className="w-full h-auto relative z-10 shadow-2xl transition-all duration-500 rounded-2xl"
                  />
                  {/* Floating badge elements */}
                  <div className="absolute -top-4 -right-4 bg-white text-gray-900 p-3 rounded-xl z-20 transform group-hover/image:translate-y-[-8px] group-hover/image:translate-x-2 transition-all duration-500 hover:shadow-lg">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-800">
                        <Clock size={20} />
                      </div>
                      <div className="ms-3">
                        <p className="text-xs text-gray-500">{t('founded')}</p>
                        <p className="font-bold text-gray-900">2015</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-4 -left-4 bg-white text-gray-900 p-3 rounded-xl z-20 transform group-hover/image:translate-y-[8px] group-hover/image:translate-x-[-2px] transition-all duration-500 hover:shadow-lg">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-800">
                        <MapPin size={20} />
                      </div>
                      <div className="ms-3">
                        <p className="text-xs text-gray-500">{t('headquarters')}</p>
                        <p className="font-bold text-gray-900">
                          {t('city_sulaymaniyah')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Cards - Modern */}
              <div
                className={`${dir === 'ltr' ? 'md:order-2' : 'md:order-1'} space-y-8`}
              >
                {/* Mission card */}
                <div className="bg-gray-50 p-8 rounded-2xl border-l-4 border-black border-t border-r border-b border-gray-100 shadow-md hover:border-gray-400 transition-all duration-500 group/mission transform hover:translate-y-[-5px] relative overflow-hidden">
                  <div
                    className={`flex items-start ${dir === 'ltr' ? 'space-x-5' : 'space-x-reverse space-x-5'}`}
                  >
                    <div className="w-16 h-16 bg-gray-100 text-gray-800 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1 group-hover/mission:scale-110 transition-transform duration-300 shadow-sm border border-gray-200">
                      <Target size={28} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-4 text-gray-900">
                        {t('mission_title')}
                      </h3>
                      <p className="text-gray-600 text-lg group-hover/mission:text-gray-700 transition-colors duration-300">
                        {t('mission_text')}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Values card */}
                <div className="bg-gray-50 p-8 rounded-2xl border-l-4 border-black border-t border-r border-b border-gray-100 shadow-md hover:border-gray-400 transition-all duration-500 group/values transform hover:translate-y-[-5px] relative overflow-hidden">
                  <div
                    className={`flex items-start ${dir === 'ltr' ? 'space-x-5' : 'space-x-reverse space-x-5'}`}
                  >
                    <div className="w-16 h-16 bg-gray-100 text-gray-800 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1 group-hover/values:scale-110 transition-transform duration-300 shadow-sm border border-gray-200">
                      <Heart size={28} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-4 text-gray-900">
                        {t('values_title')}
                      </h3>
                      <p className="text-gray-600 text-lg group-hover/values:text-gray-700 transition-colors duration-300">
                        {t('values_text')}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Team section */}
                <div className="bg-gray-50 p-8 rounded-2xl border-l-4 border-black border-t border-r border-b border-gray-100 shadow-md hover:border-gray-400 transition-all duration-500 group/team transform hover:translate-y-[-5px] relative overflow-hidden">
                  <div
                    className={`flex items-start ${dir === 'ltr' ? 'space-x-5' : 'space-x-reverse space-x-5'}`}
                  >
                    <div className="w-16 h-16 bg-gray-100 text-gray-800 rounded-2xl flex items-center justify-center flex-shrink-0 mt-1 group-hover/team:scale-110 transition-transform duration-300 shadow-sm border border-gray-200">
                      <UsersRound size={28} />
                    </div>
                    <div className="w-full">
                      <div className="flex justify-between items-center mb-5">
                        <h3 className="text-2xl font-bold text-gray-900">
                          {t('ourTeam')}
                        </h3>
                      </div>
                      {/* Team avatars with interactive hover effect */}
                      <div className="flex -space-x-4 mb-4 group-hover/team:mb-6 transition-all duration-500">
                        {testimonials.map((testimonial, i) => (
                          <div
                            key={testimonial.name}
                            className="relative transition-transform duration-300 hover:scale-125 hover:z-20"
                            style={{
                              zIndex: 10 - i,
                              transitionDelay: `${i * 50}ms`,
                            }}
                          >
                            <img
                              src={testimonial.image}
                              alt={testimonial.name}
                              title={testimonial.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-md group-hover/team:border-gray-100"
                            />
                          </div>
                        ))}
                        <div className="w-12 h-12 bg-gray-200 text-gray-700 text-xs flex items-center justify-center border-2 border-white rounded-full shadow-md transition-transform duration-300 hover:scale-125 hover:z-20">
                          +12
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 text-gray-600">
                        <div className="flex items-center">
                          <Calendar size={16} className="me-2 text-gray-700" />
                          <span>{t('tenPlusYears')}</span>
                        </div>
                        <div className="flex items-center">
                          <Globe size={16} className="me-2 text-gray-700" />
                          <span>{acrossCountriesText}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ Section - Modern Accordion */}
      <section className="py-20 md:py-28 bg-gray-50 relative overflow-hidden">
        <div className="container mx-auto px-2 md:px-4 relative">
          <div className="max-w-4xl mx-auto">
            {/* Section header */}
            <div className="mb-16 text-center">
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-800 text-white mb-4">
                {t('faq')}
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 text-gray-900">
                {t('faq_title')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('faq_subtitle')}
              </p>
            </div>
            {/* FAQ Accordion - Modern Minimalist */}
            <div className="space-y-6">
              {faqData.map((faq, index) => (
                <AnimatedItem key={index} index={index}>
                  <div className="overflow-hidden rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-300 group/faq shadow-sm hover:shadow-md">
                      <button
                        onClick={() =>
                          setActiveFaq(activeFaq === index ? -1 : index)
                        }
                        className="flex justify-between items-center w-full p-6 text-left focus:outline-none bg-white"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 group-hover/faq:text-gray-800 transition-colors duration-300">
                          {faq.question}
                        </h3>
                        <div
                          className={`w-8 h-8 flex items-center justify-center transition-all duration-300 rounded-lg ${activeFaq === index ? 'bg-gray-800 text-white rotate-180' : 'text-gray-500 bg-gray-100'}`}
                        >
                          <ChevronDown size={16} />
                        </div>
                      </button>
                      <div
                        className={`transition-all duration-300 overflow-hidden bg-gray-50 ${activeFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                      >
                        <div className="p-6 border-t border-gray-100">
                          <p className="text-gray-600">{faq.answer}</p>
                        </div>
                      </div>
                    </div>
                </AnimatedItem>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section - Keeping Black Background */}
      <section className="py-20 md:py-28 relative overflow-hidden bg-black text-white">
        <div className="container mx-auto px-2 md:px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/10 text-white backdrop-blur-sm mb-6 animate-fade-in">
              {t('contact')}
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-white animate-fade-up leading-tight">
              {t('ready_to_simplify')}
            </h2>
            <p
              className="text-lg sm:text-xl text-gray-300 mb-10 animate-fade-up"
              style={{
                animationDelay: '150ms',
              }}
            >
              {t('join_businesses')}
            </p>
            {/* Interactive buttons */}
            <div
              className="flex flex-col sm:flex-row justify-center gap-6 animate-fade-up"
              style={{
                animationDelay: '300ms',
              }}
            >
              <button onClick={() => scrollToSection('footer')} className="relative overflow-hidden border-2 border-white text-white px-8 py-4 rounded-xl hover:bg-white hover:text-black transition-all duration-300 font-medium transform hover:translate-y-[-2px]">
                <span className="relative z-10">{t('contact_us')}</span>
              </button>
              <Link to="/signup" className="relative overflow-hidden bg-white text-black px-8 py-4 rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-lg font-medium transform hover:translate-y-[-2px] group flex items-center justify-center">
                <span className="relative z-10">{t('getStarted')}</span>
                <ArrowRight
                  size={18}
                  className={`inline-block relative z-10 ${dir === 'ltr' ? 'ms-2' : 'me-2 rotate-180'} group-hover:translate-x-1 transition-transform`}
                />
              </Link>
            </div>
            {/* Floating badges */}
            <div className="mt-16 flex flex-wrap justify-center gap-4">
              <div className="bg-white/10 backdrop-blur-sm py-2 px-4 rounded-full text-white flex items-center transform hover:scale-110 transition-transform duration-300 hover:bg-white/20">
                <ShieldCheck size={16} className="me-2" />
                <span className="text-sm font-medium">
                  {t('deliverySuccessRate')}
                </span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm py-2 px-4 rounded-full text-white flex items-center transform hover:scale-110 transition-transform duration-300 hover:bg-white/20">
                <Zap size={16} className="me-2" />
                <span className="text-sm font-medium">
                  {t('support247')}
                </span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm py-2 px-4 rounded-full text-white flex items-center transform hover:scale-110 transition-transform duration-300 hover:bg-white/20">
                <Medal size={16} className="me-2" />
                <span className="text-sm font-medium">
                  {t('verifiedBusiness')}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Interactive mouse cursor effect */}
        {isDesktop && (
          <div
            className="hidden md:block absolute w-32 h-32 rounded-full bg-white/10 blur-xl pointer-events-none mix-blend-overlay"
            style={{
              left: `${mousePosition.x}px`,
              top: `${mousePosition.y}px`,
              transform: 'translate(-50%, -50%)',
              transition: 'transform 0.1s ease-out',
            }}
          ></div>
        )}
      </section>
      {/* Footer - Modern Minimalist */}
      <footer id="footer" className="bg-gray-50 text-gray-800 relative">
        <div className="container mx-auto px-2 md:px-4 relative">
          <div className="py-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
              {/* Logo and description */}
              <div className="md:col-span-1">
                <div className="mb-4">
                  <Logo variant="footer" />
                </div>
                <p className="text-gray-600 mb-6">{t('trusted_partner')}</p>
                {/* Social icons */}
                <div className="flex space-x-4 rtl:space-x-reverse">
                  {[Twitter, Facebook, Linkedin, Instagram].map((Icon, i) => (
                    <a
                      key={i}
                      href="#"
                      className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-800 hover:text-white transition-all duration-300 transform hover:scale-110"
                    >
                      <Icon size={18} />
                    </a>
                  ))}
                </div>
              </div>
              {/* Quick links */}
              <div>
                <h3 className="text-lg font-bold mb-5 text-gray-900">
                  {t('quick_links')}
                </h3>
                <ul className="space-y-3 text-gray-600">
                  {[
                    { id: 'process', label: t('howItWorks') },
                    { id: 'global-hubs', label: t('whereWeWork') },
                    { id: 'why-us', label: t('whyUs') },
                    { id: 'about', label: t('aboutUsLink') },
                  ].map((link, i) => (
                    <li key={i}>
                      <a
                        href={`#${link.id}`}
                        onClick={(e) => {
                          e.preventDefault()
                          scrollToSection(link.id)
                        }}
                        className="hover:text-gray-900 transition-colors duration-300 flex items-center group"
                      >
                        <span
                          className={`w-0 h-px bg-gray-800 group-hover:w-3 group-hover:me-2 transition-all duration-300`}
                        ></span>
                        <span>{link.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Services */}
              <div>
                <h3 className="text-lg font-bold mb-5 text-gray-900">
                  {t('services')}
                </h3>
                <ul className="space-y-3 text-gray-600">
                  {[
                    t('sourcing_title'),
                    t('shipping_title'),
                    t('customs_title'),
                    t('warehousing'),
                    t('distribution'),
                  ].map((service, i) => (
                    <li key={i}>
                      <a
                        href="#"
                        className="hover:text-gray-900 transition-colors duration-300 flex items-center group"
                      >
                        <span
                          className={`w-0 h-px bg-gray-800 group-hover:w-3 group-hover:me-2 transition-all duration-300`}
                        ></span>
                        <span>{service}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Contact info */}
              <div>
                <h3 className="text-lg font-bold mb-5 text-gray-900">
                  {t('contact')}
                </h3>
                <ul className="space-y-4 text-gray-600">
                  <li className="flex items-start hover:text-gray-900 transition-colors duration-300 group">
                    <MapPin
                      size={18}
                      className={`me-3 mt-1 flex-shrink-0 text-gray-500 group-hover:text-gray-900 group-hover:scale-110 transition-all duration-300`}
                    />
                    <span>{t('address')}</span>
                  </li>
                  <li className="flex items-start hover:text-gray-900 transition-colors duration-300 group">
                    <PhoneCall
                      size={18}
                      className={`me-3 flex-shrink-0 mt-1 text-gray-500 group-hover:text-gray-900 group-hover:scale-110 transition-all duration-300`}
                    />
                    <div className="flex flex-col space-y-1">
                      <a href="tel:+9647706437575" className="hover:underline transition-colors block text-sm font-medium">
                        +964 770 6437575
                      </a>
                      <a href="tel:+9647722544341" className="hover:underline transition-colors block text-sm font-medium">
                        +964 772 254 4341
                      </a>
                      <a href="tel:+9647734433937" className="hover:underline transition-colors block text-sm font-medium">
                        +964 773 443 3937
                      </a>
                      <a href="tel:+8619502069819" className="hover:underline transition-colors block text-sm font-medium">
                        +8619502069819 ({t('country_china')})
                      </a>
                    </div>
                  </li>
                  <li className="flex items-center hover:text-gray-900 transition-colors duration-300 group">
                    <Mail
                      size={18}
                      className={`me-3 flex-shrink-0 text-gray-500 group-hover:text-gray-900 group-hover:scale-110 transition-all duration-300`}
                    />
                    <span>{t('email')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          {/* Copyright */}
          <div className="py-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center text-gray-600 text-sm">
            <p>{t('copyright')}</p>
            <div className="flex space-x-6 rtl:space-x-reverse mt-4 md:mt-0">
              <a href="#" className="hover:text-gray-900 transition-colors">
                {t('privacyPolicy')}
              </a>
              <a href="#" className="hover:text-gray-900 transition-colors">
                {t('termsOfService')}
              </a>
              <a href="#" className="hover:text-gray-900 transition-colors">
                {t('sitemap')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
